import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { GetData, SetData } from '../auth/Auth';
import { NEW_USER_KEY } from '../config/app.config';
import { Button } from 'react-bootstrap';
import serverClient from '../api/ServerClient';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import { useLanguage } from '../context/LanguageContext';
import feather from 'feather-icons';
import { getPageMeta } from '../util/seoHelper';
import { formatAmountInput, bengaliToEnglishNumber } from '../util/helper';

function NewUserSettings() {
    const navigate = useNavigate();
    const { t, language, changeLanguage } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currency, setCurrency] = useState('BDT');
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        return localStorage.getItem('language') || 'en';
    });
    const [currentCashBalance, setCurrentCashBalance] = useState('');
    useEffect(() => {
        feather.replace();
        const isNewUser = GetData(NEW_USER_KEY);
        if (isNewUser !== 'true') {
            navigate('/');
        }
    }, [navigate]);

    const handleLanguageChange = (e) => {
        const newLanguage = e.target.value;
        setSelectedLanguage(newLanguage);
        changeLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
    };

    async function updateNewUserSettings(event) {
        event.preventDefault();
        setLoading(true);

        const data = {
            currency,
            currentCashBalance,
        };

        try {
            // API 1: Update new user settings (currency + balance)
            await serverClient.post(`/api/new-user-settings`, data);

            serverClient.put('/api/user/settings', {
                language: selectedLanguage,
            });

            // Update language context
            changeLanguage(selectedLanguage);
            localStorage.setItem('language', selectedLanguage);

            SetData(NEW_USER_KEY, false);
            setLoading(false);
            navigate('/');
        } catch (err) {
            if (!err?.response) {
                setError(t('noServerResponse'));
            } else if (err.response?.status === 400) {
                setError(t('invalidInputData'));
            } else if (err.response?.status === 404) {
                setError(t('userNotFound'));
            } else {
                setError(t('somethingWentWrong'));
            }
        }
        setLoading(false);
    }

    return (
        <div>
            <Helmet>
                <title>{getPageMeta('newUserSettings', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('newUserSettings', language).description}
                />
                <meta name="keywords" content={getPageMeta('newUserSettings', language).keywords} />
                <link rel="canonical" href={getPageMeta('newUserSettings', language).url} />
                {getPageMeta('newUserSettings', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
            </Helmet>
            <Loader loading={loading} />
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <Link to="#" className="back-btn">
                            <i className="icon" data-feather="settings"></i>
                        </Link>
                        <h2>{t('newUserSettings')}</h2>
                    </div>
                </div>
            </header>

            <section className="section-b-space">
                <div className="custom-container">
                    {/* Welcome Card */}
                    <div className="nsu-welcome-card">
                        <div className="nsu-welcome-icon">🎉</div>
                        <p className="nsu-welcome-text">{t('setupAppPreferences')}</p>
                    </div>

                    {error && <Alert alert={'danger'}>{error}</Alert>}

                    <form onSubmit={updateNewUserSettings}>
                        {/* Language Section */}
                        <div className="settings-section">
                            <div className="settings-header">
                                <div className="settings-icon language">
                                    <i className="icon" data-feather="globe"></i>
                                </div>
                                <h3 className="settings-title">{t('systemLanguage')}</h3>
                            </div>
                            <div className="settings-content">
                                <div className="nsu-field-wrapper">
                                    <select
                                        id="language"
                                        value={selectedLanguage}
                                        onChange={handleLanguageChange}
                                        className="form-select nsu-select"
                                    >
                                        <option value="en">English</option>
                                        <option value="bn">বাংলা</option>
                                    </select>
                                </div>
                                <p className="nsu-hint">🌐 {t('languageHint')}</p>
                            </div>
                        </div>

                        {/* Currency Section */}
                        <div className="settings-section">
                            <div className="settings-header">
                                <div
                                    className="settings-icon"
                                    style={{
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        color: '#f59e0b',
                                    }}
                                >
                                    <i className="icon" data-feather="dollar-sign"></i>
                                </div>
                                <h3 className="settings-title">{t('selectCurrency')}</h3>
                            </div>
                            <div className="settings-content">
                                <div className="nsu-field-wrapper">
                                    <select
                                        onChange={(e) => setCurrency(e.target.value)}
                                        id="currency"
                                        defaultValue={'BDT'}
                                        className="form-select nsu-select"
                                    >
                                        <option value={'BDT'}>{t('taka')}</option>
                                        <option value={'USD'}>{t('dollar')}</option>
                                    </select>
                                </div>
                                <p className="nsu-hint">💰 {t('currencyHint')}</p>
                            </div>
                        </div>

                        {/* Cash Balance Section */}
                        <div className="settings-section">
                            <div className="settings-header">
                                <div
                                    className="settings-icon"
                                    style={{
                                        background: 'rgba(16, 185, 129, 0.1)',
                                        color: '#10b981',
                                    }}
                                >
                                    <i className="icon" data-feather="credit-card"></i>
                                </div>
                                <h3 className="settings-title">{t('currentCashBalance')}</h3>
                            </div>
                            <div className="settings-content">
                                <div className="nsu-field-wrapper">
                                    <input
                                        required
                                        type="text"
                                        inputMode="decimal"
                                        className="form-control nsu-input"
                                        id="current_cash_balance"
                                        placeholder={t('enterCurrentCash')}
                                        value={(() => {
                                            if (
                                                currentCashBalance === '' ||
                                                currentCashBalance === '.'
                                            ) {
                                                return '';
                                            }
                                            return formatAmountInput(currentCashBalance, language);
                                        })()}
                                        onChange={(e) => {
                                            const cleanedValue = bengaliToEnglishNumber(
                                                e.target.value
                                            )
                                                .replace(/,/g, '')
                                                .replace(/[^\d.]/g, '');

                                            if (cleanedValue === '' || cleanedValue === '.') {
                                                setCurrentCashBalance('');
                                                return;
                                            }

                                            const parts = cleanedValue.split('.');
                                            if (parts.length > 2) return;
                                            if (parts[0].length > 9) return;

                                            setCurrentCashBalance(cleanedValue);
                                        }}
                                    />
                                </div>
                                <p className="nsu-hint">💡 {t('cashBalanceHint')}</p>
                            </div>
                        </div>

                        <Button type="submit" className="btn theme-btn w-100 nsu-submit-btn">
                            {t('submit')}
                        </Button>
                    </form>
                </div>
            </section>
        </div>
    );
}

export default NewUserSettings;
